import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileText,
  User,
  Key,
  Settings,
  Download,
} from 'lucide-react'

interface AuditLogEntry {
  id: string
  tenantId: string
  tenantName: string | null
  actorType: 'user' | 'api_key' | 'system'
  actorId: string | null
  actorName: string | null
  action: string
  resourceType: string
  resourceId: string | null
  details: Record<string, unknown>
  ipAddress: string | null
  createdAt: string
}

interface AuditLogResponse {
  entries: AuditLogEntry[]
  total: number
  page: number
  limit: number
}

const actionColors: Record<string, string> = {
  create: 'bg-green-500/10 text-green-500',
  update: 'bg-blue-500/10 text-blue-500',
  delete: 'bg-red-500/10 text-red-500',
  login: 'bg-purple-500/10 text-purple-500',
  logout: 'bg-gray-500/10 text-gray-400',
  default: 'bg-gray-500/10 text-gray-400',
}

export function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const limit = 50

  useEffect(() => {
    async function fetchAuditLog() {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        })
        if (search) params.append('search', search)
        if (actionFilter) params.append('action', actionFilter)
        if (resourceFilter) params.append('resourceType', resourceFilter)

        const data = await api.get<AuditLogResponse>(`/audit-log?${params}`)
        setEntries(data.entries)
        setTotal(data.total)
      } catch (error) {
        console.error('Failed to fetch audit log:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchAuditLog()
  }, [page, search, actionFilter, resourceFilter])

  const totalPages = Math.ceil(total / limit)

  const getActorIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <User className="h-4 w-4" />
      case 'api_key':
        return <Key className="h-4 w-4" />
      case 'system':
        return <Settings className="h-4 w-4" />
      default:
        return <User className="h-4 w-4" />
    }
  }

  const getActionColor = (action: string) => {
    const key = action.split('.')[0] || action
    return actionColors[key] || actionColors.default
  }

  const handleExport = async () => {
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (actionFilter) params.append('action', actionFilter)
      if (resourceFilter) params.append('resourceType', resourceFilter)

      // In a real implementation, this would trigger a download
      const data = await api.get<{ url: string }>(`/audit-log/export?${params}`)
      window.open(data.url, '_blank')
    } catch (error) {
      console.error('Failed to export audit log:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit Log</h1>
          <p className="text-muted-foreground">
            Track all actions and changes across the platform
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-input hover:bg-accent"
        >
          <Download className="h-4 w-4" />
          Export
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search audit log..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-full rounded-lg border border-input bg-background pl-10 pr-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">All Actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
        </select>
        <select
          value={resourceFilter}
          onChange={(e) => {
            setResourceFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">All Resources</option>
          <option value="tenant">Tenant</option>
          <option value="application">Application</option>
          <option value="user">User</option>
          <option value="api_key">API Key</option>
          <option value="notification">Notification</option>
          <option value="live_session">Live Session</option>
        </select>
      </div>

      {/* Log Entries */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No audit log entries found
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(entry.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded bg-muted text-muted-foreground">
                          {getActorIcon(entry.actorType)}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {entry.actorName || entry.actorId || 'System'}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {entry.actorType.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getActionColor(entry.action)}`}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium capitalize">
                          {entry.resourceType.replace('_', ' ')}
                        </p>
                        {entry.resourceId && (
                          <p className="text-xs text-muted-foreground font-mono">
                            {entry.resourceId}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {entry.tenantName || entry.tenantId || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                      {entry.ipAddress || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * limit + 1} to{' '}
              {Math.min(page * limit, total)} of {total} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="p-1 rounded hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
